import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppwriteDatabaseService, fromAppwriteDoc, fromAppwriteDocs } from '@prism/angular-shared';
import { AppwriteApiEndpoint, AppwriteApiRoute, BuildInfo, CloudFunction, CloudFunctionFlag, Collection, CollectionChangeExecutionOutcome, CronJob, DeviceDocumentReads, EnvironmentId, GlobalVariableDocumentIds, InventoryTransaction, InventoryTransactionType, SendEmailRequest, SendEmailResponse, Tag, TagCategory } from '@prism/prism-shared';
import { Query } from 'appwrite';
import { Observable, catchError, first, from, map, of, switchMap, tap } from 'rxjs';
import { AppwriteFunctionsService } from './appwrite-functions.service';
import { isAppwriteStorageUrl, normalizeAppwriteStorageUrl } from './appwrite-storage-url.util';

const TRANSLATE_FUNCTION_ID = 'translate';

@Injectable({ providedIn: 'root' })
export class SharedApiService {

    constructor(
        private appwrite: AppwriteDatabaseService,
        private http: HttpClient,
        private functions: AppwriteFunctionsService,
    ) { }

    public getTags(): Observable<Tag[]> {
        return this.appwrite.list<Tag>(Collection.tag, [Query.limit(5000)]).pipe(
            map(resp => resp.items),
        );
    }

    public getTagsByCategory(category: TagCategory): Observable<Tag[]> {
        return from(this.appwrite.collection<Tag>(Collection.tag, ref => ref.where('category', '==', category)).get()).pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        )
    }

    public createTag(tag: Tag): Observable<void> {
        return from(this.appwrite.collection<Tag>(Collection.tag).doc(tag.id).set(tag));
    }
    public addTagToEntity(tagId: string, entityId: string, collection: any): Observable<void> {
        return this.appwrite.get<any>(collection, entityId).pipe(
            switchMap(entity => this.appwrite.collection<any>(collection).doc(entityId).update({
                tagIds: Array.from(new Set([...(entity.tagIds ?? []), tagId])),
            })),
            map(() => void 0),
        )
    }
    public removeTagFromEntity(tagId: string, entityId: string, collection: any): Observable<void> {
        return this.appwrite.get<any>(collection, entityId).pipe(
            switchMap(entity => this.appwrite.collection<any>(collection).doc(entityId).update({
                tagIds: (entity.tagIds ?? []).filter((id: string) => id !== tagId),
            })),
            map(() => void 0),
        )
    }

    public googleTranslateText(translateText: string): Observable<string> {
        return this.functions.postFunction<{ translatedText: string }>(TRANSLATE_FUNCTION_ID, { translateText }).pipe(
            map((resp) => resp?.translatedText || ''),
        )
    }

    public sendEmail(request: SendEmailRequest): Observable<SendEmailResponse> {
        return this.functions.post<SendEmailResponse>(
            'api',
            `/${AppwriteApiRoute.Email}/${AppwriteApiEndpoint.SendEmail}`,
            request,
        );
    }

    /**
     * What a browser tab will actually render. Svg and html are left out deliberately - an object url
     * inherits our origin, so script inside a file a customer sent us would run as us.
     */
    private inlineViewableMimeTypes = [
        'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/apng', 'image/avif',
        'application/pdf', 'text/plain',
    ];

    /**
     * Opens a file in a new tab, falling back to a named download for anything the browser will not render.
     * The fallback matters: the tab is fed an object url, which carries no filename, so an unrenderable
     * type (a .docx, or a photo an email client mislabelled) lands in downloads as a bare guid instead.
     */
    public downloadAndOpenFileInNewTab(fileName: string, downloadUrl: string) {
        const normalizedUrl = normalizeAppwriteStorageUrl(downloadUrl);
        this.http.get(normalizedUrl, { responseType: 'blob', withCredentials: isAppwriteStorageUrl(normalizedUrl) }).pipe(
            first(),
            tap((blob) => {
                const mimeType = (blob.type || '').split(';')[0].trim().toLowerCase();

                if (!this.inlineViewableMimeTypes.includes(mimeType)) {
                    this.downloadBlobToBrowser(fileName, blob);
                    return;
                }

                const downloadLink = document.createElement('a');
                downloadLink.href = URL.createObjectURL(blob);
                downloadLink.target = '_blank'; // Open in new tab
                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);

                // Optional: Revoke the object URL after a short delay to ensure the tab opens
                setTimeout(() => URL.revokeObjectURL(downloadLink.href), 100);
            }),
            // If the fetch itself fails the click would otherwise do nothing at all, so hand the url
            // straight to the browser rather than leaving the user with no file.
            catchError(() => {
                window.open(normalizedUrl, '_blank');
                return of(void 0);
            }),
        ).subscribe();
    }

    public downloadFileToBrowser(fileName: string, downloadUrl: string) {
        const normalizedUrl = normalizeAppwriteStorageUrl(downloadUrl);
        this.http.get(normalizedUrl, { responseType: 'blob', withCredentials: isAppwriteStorageUrl(normalizedUrl) }).pipe(
            first(),
            tap((blob) => {
                this.downloadBlobToBrowser(fileName, blob);
            })
        ).subscribe()
    }

    public downloadBlobToBrowser(
        fileName: string,
        blob: Blob
    ) {
        const downloadLink = document.createElement('a');
        downloadLink.href = URL.createObjectURL(blob);
        downloadLink.download = fileName;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        URL.revokeObjectURL(downloadLink.href);
    }

    public downloadHtmlCanvasElementToBrowser(fileName: string, canvas: HTMLCanvasElement) {
        canvas.toBlob(blob => {
            if (!blob) {
                console.error('Failed to convert canvas to blob');
                return;
            }

            this.downloadBlobToBrowser(fileName, blob);
        }, 'image/png');
    }

    public downloadFileAsBlob(downloadUrl: string) {
        const normalizedUrl = normalizeAppwriteStorageUrl(downloadUrl);
        return this.http.get(normalizedUrl, { responseType: 'blob', withCredentials: isAppwriteStorageUrl(normalizedUrl) })
    }

    public getBuildInfo(): Observable<BuildInfo> {
        return from(this.appwrite.collection<BuildInfo>(Collection.globalVariables).doc(GlobalVariableDocumentIds.BuildInfo)
            .get().pipe(
                map((resp) => fromAppwriteDoc(resp))
            )
        )
    }

    public queryGrok(commandString: string): Observable<any> {
        return this.functions.post<any>('api', `/${AppwriteApiRoute.Utility}/${AppwriteApiEndpoint.QueryGrok}`, { commandString }).pipe(
            map((response) => {
                return response.res.choices[0]?.message?.content;
            }),
            catchError(err => {
                console.error(err);
                return of('');
            })
        )
    }

    /**
     * Vision query against images stored in Appwrite storage (referenced by bucket +
     * file ids; the server downloads them, so photos never ride the request payload).
     * With a schema, xAI structured outputs guarantee the reply content is JSON
     * matching it. Errors PROPAGATE (unlike queryGrok) — extraction flows must
     * distinguish failure from an empty answer.
     *
     * The endpoint also accepts inline base64 `images` for callers that hold the bytes
     * and would otherwise upload a throwaway file. From the browser prefer storage: the
     * scan is uploaded anyway, so referencing it costs nothing.
     */
    public queryGrokVision(args: {
        prompt: string;
        bucketId: string;
        fileIds: string[];
        schema?: Record<string, unknown>;
        model?: string;
    }): Observable<string> {
        return this.functions.post<any>('api', `/${AppwriteApiRoute.Utility}/${AppwriteApiEndpoint.QueryGrokVision}`, args).pipe(
            map((response) => {
                if (!response?.success) throw new Error(response?.error || 'vision query failed');
                return (response.res?.choices?.[0]?.message?.content as string) ?? '';
            }),
        )
    }

    public setDeviceDocumentReads(deviceDocumentReads: DeviceDocumentReads): Observable<void> {
        return from(this.appwrite.collection<DeviceDocumentReads>(Collection.deviceDocumentReads)
            .doc(deviceDocumentReads.id)
            .set(deviceDocumentReads))
    }

    public copyCollection(
        sourceEnvironmentId: EnvironmentId,
        targetEnvironmentId: EnvironmentId,
        collectionName: string,
    ): Observable<CollectionChangeExecutionOutcome> {
        return this.functions.post<CollectionChangeExecutionOutcome>('api', `/${AppwriteApiRoute.Migration}/${AppwriteApiEndpoint.CopyCollection}`, { sourceEnvironmentId, targetEnvironmentId, collectionName })
    }

    public deleteCollection(
        targetEnvironmentId: EnvironmentId,
        collectionName: string,
    ): Observable<CollectionChangeExecutionOutcome> {
        return this.functions.post<CollectionChangeExecutionOutcome>('api', `/${AppwriteApiRoute.Migration}/${AppwriteApiEndpoint.DeleteCollection}`, { targetEnvironmentId, collectionName })
    }

    public listRootCollections(targetEnvironmentId: EnvironmentId): Observable<string[]> {
        return this.functions.post<{ collectionNames: string[] }>('api', `/${AppwriteApiRoute.Migration}/${AppwriteApiEndpoint.ListRootCollections}`, { targetEnvironmentId }).pipe(
            map(resp => resp.collectionNames ?? [])
        );
    }

    public copyUsersBetweenEnvironments(
        sourceEnvironmentId: EnvironmentId,
        targetEnvironmentId: EnvironmentId,
    ): Observable<CollectionChangeExecutionOutcome> {
        return this.functions.post<CollectionChangeExecutionOutcome>('api', `/${AppwriteApiRoute.Migration}/${AppwriteApiEndpoint.CopyUserDataBetweenEnvironments}`, { sourceEnvironmentId, targetEnvironmentId })
    }

    public loadCronJobs(): Observable<CronJob[]> {
        return from(this.appwrite.collection<CronJob>(Collection.cronJob).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        ))
    }

    public loadCronJobByType(type: CloudFunction): Observable<CronJob> {
        return this.appwrite.collection<CronJob>(Collection.cronJob).doc(type).get().pipe(
            map(resp => {
                if (!resp.exists) {
                    return null as unknown as CronJob
                }
                return fromAppwriteDoc(resp)
            })
        )
    }

    public setCronJob(cronJob: CronJob): Observable<void> {
        return from(this.appwrite.collection(Collection.cronJob).doc(cronJob.id).set(cronJob))
    }

    public getCronJobByTypeValueChanges(type: CloudFunction): Observable<CronJob> {
        return this.appwrite.collection<CronJob>(Collection.cronJob).doc(type).valueChanges().pipe(
            map(resp => {
                if (!resp) {
                    return null as unknown as CronJob
                }
                return resp;
            })
        )
    }

    public test() {
        const startOfPreviousDay = new Date()
        startOfPreviousDay.setDate(startOfPreviousDay.getDate() - 1)
        startOfPreviousDay.setHours(0, 0, 0, 0)
        return from(this.appwrite.collection(
            Collection.plasticsSkid,
            ref => ref.where('createdOn', '>', startOfPreviousDay).where('createdOn', '<=', new Date())
        ).get())
    }


    public renderZplLabel(
        zpl: string,
        width: number = 4,
        height: number = 6,
        dpmm: string = '8dpmm',
        index: number = 0
    ): Observable<string> {
        console.log('RENDER!!!')
        if (!zpl) {
            return of('');
        }

        const url = `https://api.labelary.com/v1/printers/${dpmm}/labels/${width}x${height}/${index}/`;

        return this.http.post(url, zpl, {
            responseType: 'blob',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
        }).pipe(
            map((blob: Blob) => URL.createObjectURL(blob))
        );
    }

    public loadCloudFunctionFlags(): Observable<CloudFunctionFlag[]> {
        return from(this.appwrite.collection<CloudFunctionFlag>(Collection.cloudFunctionFlag).get()).pipe(
            map(resp => fromAppwriteDocs(resp.docs))
        )
    }
    public getCloudFunctionFlagsValueChanges(): Observable<CloudFunctionFlag[]> {
        return from(this.appwrite.collection<CloudFunctionFlag>(Collection.cloudFunctionFlag).valueChanges());
    }

    public setCloudFunctionFlag(flag: CloudFunctionFlag): Observable<void> {
        return from(this.appwrite.collection<CloudFunctionFlag>(Collection.cloudFunctionFlag).doc(flag.id).set(flag));
    }

    public setCloudFunctionFlags(flags: CloudFunctionFlag[]): Observable<void> {
        return from(Promise.all(
            flags.map(flag => this.appwrite.collection<CloudFunctionFlag>(Collection.cloudFunctionFlag).doc(flag.id).set(flag))
        )).pipe(map(() => void 0));
    }

    // =======================================================
    // Inventory Transactions
    // =======================================================

    public loadInventoryTransactionRecentByType(type: InventoryTransactionType, hoursBack: number = 24): Observable<InventoryTransaction[]> {
        const cutoff = new Date(Date.now() - hoursBack * 60 * 60 * 1000);
        return this.appwrite.list<InventoryTransaction>(Collection.inventoryTransaction, [
            Query.equal('type', type),
            Query.greaterThan('createdOn', cutoff.toISOString()),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadInventoryTransactionByEntityId(entityId: string, type: InventoryTransactionType): Observable<InventoryTransaction[]> {
        return this.appwrite.list<InventoryTransaction>(Collection.inventoryTransaction, [
            Query.equal('type', type),
            Query.equal('entityId', entityId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadInventoryTransactionByEntityIdAfterDate(entityId: string, type: InventoryTransactionType, startDate: Date): Observable<InventoryTransaction[]> {
        return this.appwrite.collection<InventoryTransaction>(
            Collection.inventoryTransaction,
            ref => ref
                .where('type', '==', type)
                .where('entityId', '==', entityId)
                .where('createdOn', '>', startDate)
        ).get().pipe(
            map(resp => fromAppwriteDocs(resp.docs))
        );
    }

    public loadInventoryTransactionByEntityIdBetween(entityId: string, type: InventoryTransactionType, startDate: Date, endDate: Date): Observable<InventoryTransaction[]> {
        return this.appwrite.collection<InventoryTransaction>(
            Collection.inventoryTransaction,
            ref => ref
                .where('type', '==', type)
                .where('entityId', '==', entityId)
                .where('createdOn', '>=', startDate)
                .where('createdOn', '<=', endDate)
        ).get().pipe(
            map(resp => fromAppwriteDocs(resp.docs))
        );
    }

    public loadInventoryTransactionByDateRange(type: InventoryTransactionType, startDate: Date, endDate: Date): Observable<InventoryTransaction[]> {
        return this.appwrite.list<InventoryTransaction>(Collection.inventoryTransaction, [
            Query.equal('type', type),
            Query.greaterThanEqual('createdOn', startDate.toISOString()),
            Query.lessThanEqual('createdOn', endDate.toISOString()),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadInventoryTransactionAll(type: InventoryTransactionType): Observable<InventoryTransaction[]> {
        return this.appwrite.collection<InventoryTransaction>(
            Collection.inventoryTransaction,
            ref => ref.where('type', '==', type)
        ).get().pipe(
            map(resp => resp.docs.map((d: any) => ({ ...d.data(), id: d.id }) as InventoryTransaction))
        );
    }

    public getInventoryTransactionByEntityIdValueChanges(entityId: string, type: InventoryTransactionType): Observable<InventoryTransaction[]> {
        return this.appwrite.collection<InventoryTransaction>(
            Collection.inventoryTransaction,
            (ref: any) => ref
                .where('type', '==', type)
                .where('entityId', '==', entityId)
        ).valueChanges();
    }

    public getInventoryTransactionFromDateValueChanges(type: InventoryTransactionType, date: Date): Observable<InventoryTransaction[]> {
        return this.appwrite.collection<InventoryTransaction>(
            Collection.inventoryTransaction,
            (ref: any) => ref
                .where('type', '==', type)
                .where('createdOn', '>=', date)
        ).valueChanges();
    }

    public setInventoryTransaction(transaction: InventoryTransaction): Observable<void> {
        return from(this.appwrite.collection<InventoryTransaction>(Collection.inventoryTransaction).doc(transaction.id).set(transaction));
    }

    public loadInventoryTransactionByEntityIdRecent(entityId: string, type: InventoryTransactionType, limit: number = 100): Observable<InventoryTransaction[]> {
        return this.appwrite.collection<InventoryTransaction>(
            Collection.inventoryTransaction,
            ref => ref
                .where('type', '==', type)
                .where('entityId', '==', entityId)
                .orderBy('createdOn', 'desc')
                .limit(limit)
        ).get().pipe(
            map(resp => resp.docs.map((d: any) => ({ ...d.data(), id: d.id }) as InventoryTransaction))
        );
    }

    public loadInventoryTransactionByEntityIdWithChangeFilter(entityId: string, type: InventoryTransactionType, changeValue: number, limit: number = 50): Observable<InventoryTransaction[]> {
        return this.appwrite.list<InventoryTransaction>(Collection.inventoryTransaction, [
            Query.equal('type', type),
            Query.equal('entityId', entityId),
            Query.equal('change', changeValue),
            Query.orderDesc('createdOn'),
            Query.limit(limit),
        ]).pipe(
            map(resp => resp.items),
        );
    }
}
